import { formatPrice, type ClientBalance, type Purchase } from '@diana/shared'
import { Status, type Tone } from '../../classroom/ui.js'

/*
 * Small pieces the Uczniowie and Rozliczenia screens share: what package a
 * student is on, how many lessons are left in it, and the one-word standing
 * in the „Saldo” column (Studio-Clients.png, Studio-Accounting.png).
 */

/** Polish plural: 1 lekcja, 2 lekcje, 5 lekcji. */
export const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? few : many

export const lessonsWord = (n: number) => plural(n, 'lekcja', 'lekcje', 'lekcji')

/** The student's most recent package (not single lessons), if any. */
export function lastPackage(clientId: string, purchases: readonly Purchase[]): Purchase | undefined {
  let best: Purchase | undefined
  for (const p of purchases) {
    if (p.clientId !== clientId || p.kind !== 'package') continue
    if (!best || p.purchasedAt > best.purchasedAt) best = p
  }
  return best
}

/**
 * „6 z 10 lekcji” — what is left, out of the package she is on. When more is
 * left than the last package holds (two bought at once), just the count.
 * Prepaid single lessons show as a count too; everyone else „—”.
 */
export function packageLeft(b: ClientBalance | undefined, pkg: Purchase | undefined, withSize: boolean): string {
  if (!b || b.lessonsBought === 0) return '—'
  const left = Math.max(0, b.lessonsRemaining)
  if (pkg) {
    if (withSize && left <= pkg.lessons) return `${left} z ${pkg.lessons} ${lessonsWord(pkg.lessons)}`
    return `${left} ${lessonsWord(left)}`
  }
  return left > 0 ? `${left} ${lessonsWord(left)}` : '—'
}

/** „21.10” from 'YYYY-MM-DD'. */
export const dayMonth = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`

/**
 * Where a student stands, as a dot and a few words:
 * owes money → „do zapłaty 120 zł”; package used up → `usedUp`; no lessons
 * and nothing bought yet → „nowy”; otherwise „rozliczony”.
 *
 * The words are in the generic masculine, like „Uczeń” and „Dodaj ucznia”
 * on the same screen — the books do not know anyone's gender.
 */
export function standing(
  b: ClientBalance | undefined,
  pkg: Purchase | undefined,
  { usedUp, nextLesson }: { usedUp: string; nextLesson?: string },
): { tone: Tone; word: string; title?: string } {
  if (!b || (b.lessonsDone === 0 && b.lessonsBought === 0 && b.balanceGrosz === 0)) {
    return { tone: 'neutral', word: nextLesson ? `nowy · lekcja ${dayMonth(nextLesson)}` : 'nowy' }
  }
  if (b.balanceGrosz < 0) return { tone: 'wait', word: `do zapłaty ${formatPrice(-b.balanceGrosz)}` }
  const title = b.balanceGrosz > 0 ? `Przedpłacone: ${formatPrice(b.balanceGrosz)}` : undefined
  if (pkg && b.lessonsRemaining <= 0) return { tone: 'bad', word: usedUp, title }
  return { tone: 'ok', word: 'rozliczony', title }
}

export function Standing(props: Parameters<typeof standing>[2] & { b: ClientBalance | undefined; pkg: Purchase | undefined }) {
  const s = standing(props.b, props.pkg, props)
  return (
    <span title={s.title}>
      <Status tone={s.tone} className="whitespace-nowrap">
        {s.word}
      </Status>
    </span>
  )
}

/* ── table cells, as the Studio tables draw them ───────────────────────── */

/** Header cell: 12px, 600, grey, no uppercase. */
export const th = 'px-4 pb-3 text-class-label font-semibold text-class-ink-faint first:pl-0 last:pr-0'
/** Body cell: a 60px row with a hairline on top. */
export const td = 'h-[60px] border-t border-class-line px-4 py-2 first:pl-0 last:pr-0'
